import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GaugeCircle } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { capacidad_desde: 1, capacidad_hasta: 1, horas_semanales: 1 };

function ReglasUsoSalasImpl({ embedded = false }, ref) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editing, setEditing] = useState(null);

    const load = () => api.get("/reglas-uso-salas").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    useImperativeHandle(ref, () => ({ openNew }), []);
    const openEdit = (r) => {
        setEditing(r.regla_id);
        setForm({
            capacidad_desde: r.capacidad_desde,
            capacidad_hasta: r.capacidad_hasta,
            horas_semanales: r.horas_semanales,
        });
        setOpen(true);
    };

    const submit = async () => {
        const desde = parseInt(form.capacidad_desde, 10);
        const hasta = parseInt(form.capacidad_hasta, 10);
        const horas = parseFloat(form.horas_semanales);
        if (!desde || !hasta || desde < 1) return toast.error("Rango de capacidad inválido");
        if (hasta < desde) return toast.error("'Hasta' debe ser ≥ 'Desde'");
        if (!horas || horas <= 0) return toast.error("Horas semanales debe ser mayor a 0");
        const payload = { capacidad_desde: desde, capacidad_hasta: hasta, horas_semanales: horas };
        try {
            if (editing) {
                await api.patch(`/reglas-uso-salas/${editing}`, payload);
                toast.success("Regla actualizada");
            } else {
                await api.post("/reglas-uso-salas", payload);
                toast.success("Regla creada");
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (r) => {
        if (!window.confirm(`¿Eliminar la regla ${r.capacidad_desde}–${r.capacidad_hasta}?`)) return;
        try {
            await api.delete(`/reglas-uso-salas/${r.regla_id}`);
            toast.success("Eliminada");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="max-w-2xl">
            {!embedded && (
                <PageHeader
                    title="Reglas de uso de salas"
                    subtitle="Cupo semanal de horas según la capacidad de cada oficina."
                />
            )}

            {!embedded && (
                <div className="flex justify-end mb-3">
                    <Button onClick={openNew} data-testid="regla-new">
                        <Plus className="h-4 w-4 mr-1" /> Nueva regla
                    </Button>
                </div>
            )}

            {items.length === 0 ? (
                <EmptyState
                    title="Sin reglas cargadas"
                    description="Creá la primera regla. Si una oficina no tiene regla aplicable no podrá reservar."
                />
            ) : (
                <div className="grid gap-2">
                    {items.map((r) => (
                        <Card key={r.regla_id} data-testid={`regla-${r.regla_id}`}>
                            <CardContent className="p-4 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="h-10 w-10 rounded-md bg-primary-soft text-primary grid place-items-center shrink-0">
                                        <GaugeCircle className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-heading font-semibold leading-tight">
                                            {r.capacidad_desde === r.capacidad_hasta
                                                ? `${r.capacidad_desde} persona${r.capacidad_desde === 1 ? "" : "s"}`
                                                : `${r.capacidad_desde} a ${r.capacidad_hasta} personas`}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            Cupo: <span className="font-mono font-semibold text-foreground">{r.horas_semanales} h</span> por semana
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`regla-edit-${r.regla_id}`}>
                                        <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => remove(r)} data-testid={`regla-del-${r.regla_id}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editing ? "Editar regla" : "Nueva regla"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs">Capacidad desde</Label>
                                <Input
                                    type="number" min="1"
                                    value={form.capacidad_desde}
                                    onChange={(e) => setForm({ ...form, capacidad_desde: e.target.value })}
                                    data-testid="regla-desde"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs">Capacidad hasta</Label>
                                <Input
                                    type="number" min="1"
                                    value={form.capacidad_hasta}
                                    onChange={(e) => setForm({ ...form, capacidad_hasta: e.target.value })}
                                    data-testid="regla-hasta"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs">Horas semanales</Label>
                            <Input
                                type="number" min="0.5" step="0.5"
                                value={form.horas_semanales}
                                onChange={(e) => setForm({ ...form, horas_semanales: e.target.value })}
                                data-testid="regla-horas"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            Los rangos no pueden solaparse. Si una oficina cae fuera de toda regla, no podrá reservar.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} data-testid="regla-submit">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const ReglasUsoSalas = forwardRef(ReglasUsoSalasImpl);
export default ReglasUsoSalas;
